import axios, { AxiosInstance, AxiosError, AxiosResponse, AxiosRequestConfig } from 'axios';

/**
 * API Error Response Type
 */
export interface ApiErrorResponse {
  detail?: string | Record<string, string[]>;
  message?: string;
  error?: string;
  status?: number;
}

/**
 * API Client Configuration
 */
export interface ApiClientConfig {
  baseURL: string;
  timeout?: number;
  withCredentials?: boolean;
}

/**
 * Loading state type
 */
export type LoadingState = 'idle' | 'loading' | 'success' | 'error';

/**
 * API Response wrapper
 */
export interface ApiResponse<T> {
  data?: T;
  status: number;
  message?: string;
}

/**
 * Pagination metadata
 */
export interface PaginationMeta {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

/**
 * Paginated response
 */
export interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationMeta;
}

class ApiClient {
  private axiosInstance: AxiosInstance;
  private loadingState: LoadingState = 'idle';
  private loadingCallbacks: Set<(state: LoadingState) => void> = new Set();
  private errorCallbacks: Set<(error: ApiErrorResponse) => void> = new Set();

  constructor(config: ApiClientConfig) {
    this.axiosInstance = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout || 30000,
      withCredentials: config.withCredentials ?? true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * Set up request and response interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Add authorization token if available
        const token = this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Update loading state
        this.setLoadingState('loading');

        return config;
      },
      (error) => {
        this.setLoadingState('error');
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response) => {
        this.setLoadingState('success');
        return response;
      },
      (error: AxiosError<ApiErrorResponse>) => {
        this.setLoadingState('error');

        // Handle specific error cases
        if (error.response?.status === 401) {
          // Unauthorized - clear token and redirect to login
          this.clearAuthToken();
          this.notifyError({
            message: 'Session expired. Please log in again.',
            status: 401,
          });
          // You can dispatch a logout action or navigate here if needed
        } else if (error.response?.status === 403) {
          // Forbidden
          this.notifyError({
            message: 'You do not have permission to perform this action.',
            status: 403,
          });
        } else if (error.response?.status === 404) {
          // Not found
          this.notifyError({
            message: 'The requested resource was not found.',
            status: 404,
          });
        } else if (error.response?.status === 422) {
          // Validation error
          const detail = error.response.data?.detail;
          this.notifyError({
            detail,
            message: 'Validation error. Please check your input.',
            status: 422,
          });
        } else if (error.response?.status === 500) {
          // Server error
          this.notifyError({
            message: 'Server error. Please try again later.',
            status: 500,
          });
        } else if (error.response) {
          // Other error responses
          this.notifyError({
            detail: error.response.data?.detail,
            message: error.response.data?.message || 'An error occurred.',
            status: error.response.status,
          });
        } else if (error.request) {
          // No response received
          this.notifyError({
            message:
              'No response from server. Please check your connection.',
            status: 0,
          });
        } else {
          // Error in request setup
          this.notifyError({
            message: error.message || 'An unexpected error occurred.',
            status: 0,
          });
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Subscribe to loading state changes
   */
  public onLoadingStateChange(callback: (state: LoadingState) => void): void {
    this.loadingCallbacks.add(callback);
  }

  /**
   * Unsubscribe from loading state changes
   */
  public offLoadingStateChange(callback: (state: LoadingState) => void): void {
    this.loadingCallbacks.delete(callback);
  }

  /**
   * Subscribe to error notifications
   */
  public onError(callback: (error: ApiErrorResponse) => void): void {
    this.errorCallbacks.add(callback);
  }

  /**
   * Unsubscribe from error notifications
   */
  public offError(callback: (error: ApiErrorResponse) => void): void {
    this.errorCallbacks.delete(callback);
  }

  /**
   * Set loading state and notify subscribers
   */
  private setLoadingState(state: LoadingState): void {
    this.loadingState = state;
    this.loadingCallbacks.forEach((callback) => callback(state));
  }

  /**
   * Notify error subscribers
   */
  private notifyError(error: ApiErrorResponse): void {
    this.errorCallbacks.forEach((callback) => callback(error));
  }

  /**
   * Get current loading state
   */
  public getLoadingState(): LoadingState {
    return this.loadingState;
  }

  /**
   * Get or set auth token
   */
  private getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  /**
   * Clear auth token
   */
  private clearAuthToken(): void {
    localStorage.removeItem('auth_token');
  }

  /**
   * Set auth token
   */
  public setAuthToken(token: string): void {
    localStorage.setItem('auth_token', token);
  }

  /**
   * GET request
   */
  public async get<T = unknown>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.get<T>(url, config);
  }

  /**
   * POST request
   */
  public async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.post<T>(url, data, config);
  }

  /**
   * PUT request
   */
  public async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.put<T>(url, data, config);
  }

  /**
   * PATCH request
   */
  public async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.patch<T>(url, data, config);
  }

  /**
   * DELETE request
   */
  public async delete<T = unknown>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.delete<T>(url, config);
  }

  /**
   * Upload file with progress tracking
   */
  public async uploadFile<T = unknown>(
    url: string,
    file: File,
    onProgress?: (progress: number) => void,
    additionalData?: Record<string, string | Blob>
  ): Promise<AxiosResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    // Add additional data fields
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    return this.axiosInstance.post<T>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress?.(progress);
        }
      },
    });
  }

  /**
   * Download file
   */
  public async downloadFile(
    url: string,
    filename: string
  ): Promise<void> {
    const response = await this.axiosInstance.get(url, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data]);
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(link.href);
  }

  /**
   * Get axios instance for advanced usage
   */
  public getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

/**
 * Create and export API client instance
 */
const apiClient = new ApiClient({
  // `??`, not `||`: an empty VITE_API_URL is meaningful. It leaves request URLs
  // relative (endpoint paths already start with /api), so the dev server proxies
  // them to the backend instead of the browser making a cross-origin call.
  baseURL: import.meta.env.VITE_API_URL ?? 'http://localhost:8000',
  timeout: 30000,
  withCredentials: true,
});

export default apiClient;
export { ApiClient };
